import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useEventTypes,
  useCreateEventType,
  useUpdateEventType,
  useDeleteEventType,
} from '../../hooks/useEventTypes';
import { Button } from '../../components/common/Button';
import { Spinner } from '../../components/common/Spinner';
import { ConfirmModal } from '../../components/common/Modal';

export default function EventTypePage() {
  const navigate = useNavigate();
  const { data: types, isLoading } = useEventTypes();
  const createMutation = useCreateEventType();
  const updateMutation = useUpdateEventType();
  const deleteMutation = useDeleteEventType();

  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState('');
  const [maxParticipants, setMaxParticipants] = useState<string>('');
  const [rsvpDeadlineDays, setRsvpDeadlineDays] = useState<string>('');
  const [editingMaxParticipants, setEditingMaxParticipants] = useState<string>('');
  const [editingRsvpDeadlineDays, setEditingRsvpDeadlineDays] = useState<string>('');

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    try {
      await createMutation.mutateAsync({
        name: name.trim(),
        maxParticipants: maxParticipants ? parseInt(maxParticipants, 10) : null,
        rsvpDeadlineDays: rsvpDeadlineDays ? parseInt(rsvpDeadlineDays, 10) : null,
      });
      setShowForm(false);
      setName('');
      setMaxParticipants('');
      setRsvpDeadlineDays('');
    } catch (err) {
      setFormError((err as Error).message);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingId === null) return;
    try {
      await updateMutation.mutateAsync({
        id: editingId,
        name: editingName.trim(),
        maxParticipants: editingMaxParticipants ? parseInt(editingMaxParticipants, 10) : null,
        rsvpDeadlineDays: editingRsvpDeadlineDays ? parseInt(editingRsvpDeadlineDays, 10) : null,
      });
      setEditingId(null);
      setEditingName('');
      setEditingMaxParticipants('');
      setEditingRsvpDeadlineDays('');
    } catch (err) {
      setFormError((err as Error).message);
    }
  };

  const handleDelete = async () => {
    if (deleteId === null) return;
    setDeleteError(null);
    try {
      await deleteMutation.mutateAsync(deleteId);
      setDeleteId(null);
    } catch (err) {
      setDeleteError((err as Error).message);
    }
  };

  if (isLoading) {
    return <div className="flex justify-center py-8"><Spinner /></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <button onClick={() => navigate('/admin')} className="text-sm text-gray-500 hover:text-gray-700 mb-1">
          &larr; Back to Admin
        </button>
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold text-gray-900">Event Types</h1>
          <Button size="sm" onClick={() => setShowForm(!showForm)}>
            {showForm ? 'Cancel' : 'Add Type'}
          </Button>
        </div>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
          {formError && <p className="text-sm text-red-600">{formError}</p>}
          <div className="flex gap-3 items-center">
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Type name *"
              className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
            <input
              type="number"
              min="1"
              value={maxParticipants}
              onChange={(e) => setMaxParticipants(e.target.value)}
              placeholder="Max participants"
              className="w-40 rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
            <input
              type="number"
              min="1"
              value={rsvpDeadlineDays}
              onChange={(e) => setRsvpDeadlineDays(e.target.value)}
              placeholder="RSVP deadline days"
              className="w-44 rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
            <Button type="submit" size="sm" isLoading={createMutation.isPending}>Create</Button>
          </div>
        </form>
      )}

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Max Participants</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">RSVP Deadline</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Events</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {types?.map((type) => (
              <tr key={type.id} className="hover:bg-gray-50">
                {editingId === type.id ? (
                  <>
                    <td className="px-6 py-2" colSpan={4}>
                      <form onSubmit={handleUpdate} className="flex gap-2 items-center">
                        <input
                          type="text"
                          required
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          className="flex-1 rounded border border-gray-300 px-2 py-1 text-sm"
                          autoFocus
                        />
                        <input
                          type="number"
                          min="1"
                          value={editingMaxParticipants}
                          onChange={(e) => setEditingMaxParticipants(e.target.value)}
                          placeholder="Max"
                          className="w-24 rounded border border-gray-300 px-2 py-1 text-sm"
                        />
                        <input
                          type="number"
                          min="1"
                          value={editingRsvpDeadlineDays}
                          onChange={(e) => setEditingRsvpDeadlineDays(e.target.value)}
                          placeholder="RSVP days"
                          className="w-28 rounded border border-gray-300 px-2 py-1 text-sm"
                        />
                        <Button type="submit" size="sm" isLoading={updateMutation.isPending}>Save</Button>
                        <Button type="button" size="sm" variant="ghost" onClick={() => setEditingId(null)}>Cancel</Button>
                      </form>
                    </td>
                    <td></td>
                  </>
                ) : (
                  <>
                    <td className="px-6 py-3 text-sm font-medium text-gray-900">{type.name}</td>
                    <td className="px-6 py-3 text-sm text-gray-500">{type.maxParticipants ?? '—'}</td>
                    <td className="px-6 py-3 text-sm text-gray-500">{type.rsvpDeadlineDays ? `${type.rsvpDeadlineDays} days` : '—'}</td>
                    <td className="px-6 py-3 text-sm text-gray-500">{type.eventCount}</td>
                    <td className="px-6 py-3 text-right space-x-3">
                      <button
                        onClick={() => {
                          setEditingId(type.id);
                          setEditingName(type.name);
                          setEditingMaxParticipants(type.maxParticipants?.toString() ?? '');
                          setEditingRsvpDeadlineDays(type.rsvpDeadlineDays?.toString() ?? '');
                        }}
                        className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => { setDeleteId(type.id); setDeleteError(null); }}
                        className="text-red-600 hover:text-red-800 text-sm font-medium"
                      >
                        Delete
                      </button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ConfirmModal
        isOpen={deleteId !== null}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Delete Event Type"
        message="Are you sure you want to delete this event type?"
        confirmText="Delete"
        variant="danger"
        isLoading={deleteMutation.isPending}
        error={deleteError}
      />
    </div>
  );
}
